import React                   from 'react';
import debounce                from 'lodash/debounce';
import View                    from '@widesk-ui/components/View';
import Select                  from '@widesk-ui/components/form/Select';
import { CheckCircleOutlined } from '@ant-design/icons';
import { SearchOutlined }      from '@ant-design/icons';
import { AddressSearchResult } from '@tokamap/core';
import { geocodeAddress }      from '@tokamap/core';
import { splitLabel }          from '@tokamap/core';
import useDesignTokens         from '@/hooks/useDesignTokens';

const SEARCH_DELAY_MS = 400;

export default function PlaceSearchField({ value, onChange, autoFocus, onBlur, hint = 'L\'adresse ne sert qu\'à trouver le lieu : seules ses coordonnées sont enregistrées.' }: {
	value: AddressSearchResult | null;
	onChange: (place: AddressSearchResult | null) => void;
	autoFocus?: boolean;
	onBlur?: () => void;
	hint?: string | null;
}) {
	const tokens = useDesignTokens();
	const [results, setResults] = React.useState<AddressSearchResult[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [query, setQuery] = React.useState('');
	const controller = React.useRef<AbortController | null>(null);

	const search = React.useMemo(() => debounce(async (query: string) => {
		controller.current?.abort();

		if (!query.trim()) {
			setResults([]);
			setLoading(false);
			return;
		}

		const current = new AbortController();
		controller.current = current;

		try {
			const found = await geocodeAddress(query, current.signal);
			if (controller.current === current) setResults(found);
		} catch {
			if (controller.current === current) setResults([]);
		} finally {
			if (controller.current === current) setLoading(false);
		}
	}, SEARCH_DELAY_MS), []);

	const onSearch = (text: string) => {
		setQuery(text);
		setLoading(!!text.trim());
		search(text);
	};

	React.useEffect(() => () => {
		search.cancel();
		controller.current?.abort();
	}, [search]);

	if (value) {
		const { title, subtitle } = splitLabel(value.label);

		return (
			<View row centerV gap="xs" p="xs" br={8} bg={`color-mix(in srgb, ${tokens.color.success} 10%, transparent)`}>
				<CheckCircleOutlined style={{ color: tokens.color.success }} />

				<View flex gap={2}>
					<View bold size="small">{title}</View>
					<View size={12} color={tokens.text.muted}>
						{subtitle ? `${subtitle} · ` : ''}{value.center[1].toFixed(4)}, {value.center[0].toFixed(4)}
					</View>
				</View>

				<View size="small" pointer color={tokens.color.primary} onClick={() => onChange(null)}>
					Changer
				</View>
			</View>
		);
	}

	return (
		<View gap={4} style={{ width: '100%' }}>
			<Select
				showSearch
				filterOption={false}
				placeholder="Rechercher une adresse, une ville, un lieu…"
				autoFocus={autoFocus}
				onBlur={() => {
					onSearch('');
					onBlur?.();
				}}
				loading={loading}
				suffixIcon={<SearchOutlined />}
				notFoundContent={!query.trim() ? null : loading ? 'Recherche…' : 'Aucun lieu trouvé'}
				onSearch={onSearch}
				onChange={index => onChange(results[Number(index)] ?? null)}
				options={results.map((result, index) => ({ value: index, label: result.label }))}
			/>

			{!!hint && (
				<View size={12} color={tokens.text.muted} lineHeight={1.4}>
					{hint}
				</View>
			)}
		</View>
	);
}
